import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Users, Home, Calendar, FileText, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import AgentSitesAdmin from '@/components/admin/AgentSitesAdmin';
import AgentOfTheMonthAdmin from '@/components/admin/AgentOfTheMonthAdmin';
import CompliancePackagesAdmin from '@/components/admin/CompliancePackagesAdmin';
import AgentCredentialsAdmin from '@/components/admin/AgentCredentialsAdmin';
import LeadRoutingAdmin from '@/components/admin/LeadRoutingAdmin';
import HoaAdmin from '@/components/admin/HoaAdmin';
import BlogAutoDrafts from '@/components/admin/BlogAutoDrafts';
import PBDashboard from '@/components/PBDashboard';

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [applications, setApplications] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [buyerInquiries, setBuyerInquiries] = useState([]);
  const [sellerInquiries, setSellerInquiries] = useState([]);
  const [leadCaptures, setLeadCaptures] = useState([]);
  const [newProperty, setNewProperty] = useState({
    title: '',
    description: '',
    price: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    bedrooms: '',
    bathrooms: '',
    square_feet: '',
    property_type: 'house',
    listing_type: 'sale',
    images: []
  });
  const [newBlogPost, setNewBlogPost] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'market_insights',
    author: '',
    image_url: '',
    published: false
  });
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    file_url: '',
    category: 'policy',
    uploaded_by: 'Admin'
  });
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'instagram_post',
    canva_link: '',
    file: null
  });
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, appsRes, bookingsRes, contactRes, buyerRes, sellerRes, leadRes, blogRes, draftsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/agent-applications'),
        api.get('/bookings'),
        api.get('/admin/contact-submissions'),
        api.get('/admin/buyer-inquiries'),
        api.get('/admin/seller-inquiries'),
        api.get('/admin/lead-captures'),
        api.get('/blog'),
        api.get('/admin/blog/drafts')
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data);
      setBookings(bookingsRes.data);
      setContactSubmissions(contactRes.data);
      setBuyerInquiries(buyerRes.data);
      setSellerInquiries(sellerRes.data);
      setLeadCaptures(leadRes.data);
      setBlogPosts([...draftsRes.data, ...blogRes.data]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      await api.post('/properties', {
        ...newProperty,
        price: parseFloat(newProperty.price),
        bedrooms: parseInt(newProperty.bedrooms),
        bathrooms: parseFloat(newProperty.bathrooms),
        square_feet: parseInt(newProperty.square_feet)
      });
      toast.success('Property created successfully!');
      setNewProperty({
        title: '',
        description: '',
        price: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        bedrooms: '',
        bathrooms: '',
        square_feet: '',
        property_type: 'house',
        listing_type: 'sale',
        images: []
      });
    } catch (error) {
      toast.error('Failed to create property');
    }
  };

  const handleCreateBlogPost = async (e) => {
    e.preventDefault();
    try {
      await api.post('/blog', newBlogPost);
      toast.success('Blog post created successfully!');
      setNewBlogPost({
        title: '',
        content: '',
        excerpt: '',
        category: 'market_insights',
        author: '',
        image_url: '',
        published: false
      });
      loadDashboardData(); // Refresh to show new post
    } catch (error) {
      toast.error('Failed to create blog post');
    }
  };

  const handleUpdateBlogPost = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/blog/${editingBlog.id}`, editingBlog);
      toast.success('Blog post updated successfully!');
      setEditingBlog(null);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update blog post');
    }
  };

  const handleDeleteBlogPost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success('Blog post deleted!');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    try {
      await api.post('/documents', newDocument);
      toast.success('Document uploaded successfully!');
      setNewDocument({
        title: '',
        description: '',
        file_url: '',
        category: 'policy',
        uploaded_by: 'Admin'
      });
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage your brokerage operations</p>
          </div>
          <Button asChild className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="view-analytics-btn">
            <a href="/admin/analytics">View Analytics →</a>
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Properties</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Home className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.total_properties || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Agents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.total_agents || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.pending_bookings || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Blog Posts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.total_blog_posts || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.total_applications || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-[#BCA480] mr-2" />
                <span className="text-2xl font-bold">{stats.total_documents || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="pb" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto justify-start gap-1">
            <TabsTrigger value="pb" data-testid="pb-tab">🛡️ Compliance</TabsTrigger>
            <TabsTrigger value="hoa" data-testid="hoa-tab">🏢 HOA</TabsTrigger>
            <TabsTrigger value="routing" data-testid="routing-tab">🎯 Lead Routing</TabsTrigger>
            <TabsTrigger value="credentials" data-testid="credentials-tab">🔑 Agent Credentials</TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">Messages & Leads</TabsTrigger>
            <TabsTrigger value="properties" data-testid="properties-tab">Properties</TabsTrigger>
            <TabsTrigger value="blog" data-testid="blog-tab">Blog</TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">Templates</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">Documents</TabsTrigger>
            <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>
            <TabsTrigger value="agent-sites" data-testid="agent-sites-tab">Agent Websites</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
          </TabsList>

          {/* Principal Broker Compliance Dashboard */}
          <TabsContent value="pb">
            <div className="space-y-6">
              <PBDashboard />
              <CompliancePackagesAdmin />
            </div>
          </TabsContent>

          {/* Lead Routing */}
          <TabsContent value="routing">
            <LeadRoutingAdmin />
          </TabsContent>

          {/* HOA Admin — Phase 1a */}
          <TabsContent value="hoa">
            <HoaAdmin />
          </TabsContent>

          {/* Agent Credentials / Password Resets */}
          <TabsContent value="credentials">
            <AgentCredentialsAdmin />
          </TabsContent>

          {/* Messages & Leads Tab */}
          <TabsContent value="messages">
            <div className="space-y-6">
              {/* Contact Submissions */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Form Submissions ({contactSubmissions.length})</CardTitle>
                  <CardDescription>Messages from the contact page</CardDescription>
                </CardHeader>
                <CardContent>
                  {contactSubmissions.length > 0 ? (
                    <div className="space-y-4">
                      {contactSubmissions.map((submission) => (
                        <div key={submission.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold">{submission.name}</h4>
                              <p className="text-sm text-gray-600">{submission.email}</p>
                              {submission.phone && <p className="text-sm text-gray-600">{submission.phone}</p>}
                            </div>
                            <span className="text-xs bg-[#BCA480] text-white px-2 py-1 rounded">{submission.department}</span>
                          </div>
                          <p className="text-sm mt-2">{submission.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(submission.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No contact submissions yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Lead Captures */}
              <Card>
                <CardHeader>
                  <CardTitle>Lead Captures ({leadCaptures.length})</CardTitle>
                  <CardDescription>Emails captured from resource downloads</CardDescription>
                </CardHeader>
                <CardContent>
                  {leadCaptures.length > 0 ? (
                    <div className="space-y-4">
                      {leadCaptures.map((lead) => (
                        <div key={lead.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold">{lead.name}</h4>
                              <p className="text-sm text-gray-600">{lead.email}</p>
                              <p className="text-sm text-gray-500 mt-1">Resource: {lead.resource_title}</p>
                            </div>
                            <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">{lead.source}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(lead.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No lead captures yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Buyer Inquiries */}
              <Card>
                <CardHeader>
                  <CardTitle>Buyer Inquiries ({buyerInquiries.length})</CardTitle>
                  <CardDescription>Home buyer consultation requests</CardDescription>
                </CardHeader>
                <CardContent>
                  {buyerInquiries.length > 0 ? (
                    <div className="space-y-4">
                      {buyerInquiries.map((inquiry) => (
                        <div key={inquiry.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold">{inquiry.name}</h4>
                              <p className="text-sm text-gray-600">{inquiry.email} • {inquiry.phone}</p>
                            </div>
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">{inquiry.timeline}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                            <p><span className="font-semibold">Budget:</span> ${inquiry.budget}</p>
                            <p><span className="font-semibold">Location:</span> {inquiry.preferred_location}</p>
                            <p><span className="font-semibold">Bedrooms:</span> {inquiry.bedrooms}</p>
                            <p><span className="font-semibold">Type:</span> {inquiry.property_type}</p>
                          </div>
                          {inquiry.additional_info && (
                            <p className="text-sm mt-2 text-gray-600">{inquiry.additional_info}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(inquiry.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No buyer inquiries yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Seller Inquiries */}
              <Card>
                <CardHeader>
                  <CardTitle>Seller Inquiries ({sellerInquiries.length})</CardTitle>
                  <CardDescription>Home valuation and selling requests</CardDescription>
                </CardHeader>
                <CardContent>
                  {sellerInquiries.length > 0 ? (
                    <div className="space-y-4">
                      {sellerInquiries.map((inquiry) => (
                        <div key={inquiry.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold">{inquiry.name}</h4>
                              <p className="text-sm text-gray-600">{inquiry.email} • {inquiry.phone}</p>
                            </div>
                            <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded">{inquiry.timeline}</span>
                          </div>
                          <p className="text-sm font-semibold mt-2">{inquiry.property_address}</p>
                          <div className="grid grid-cols-3 gap-2 text-sm mt-2">
                            <p><span className="font-semibold">Type:</span> {inquiry.property_type}</p>
                            <p><span className="font-semibold">Beds:</span> {inquiry.bedrooms}</p>
                            <p><span className="font-semibold">Baths:</span> {inquiry.bathrooms}</p>
                          </div>
                          {inquiry.reason_for_selling && (
                            <p className="text-sm mt-2"><span className="font-semibold">Reason:</span> {inquiry.reason_for_selling}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(inquiry.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No seller inquiries yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card>
              <CardHeader>
                <CardTitle>Add New Property</CardTitle>
                <CardDescription>Create mock property listings</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateProperty} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Property Title *</Label>
                      <Input
                        id="title"
                        value={newProperty.title}
                        onChange={(e) => setNewProperty({...newProperty, title: e.target.value})}
                        required
                        data-testid="property-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="price">Price *</Label>
                      <Input
                        id="price"
                        type="number"
                        value={newProperty.price}
                        onChange={(e) => setNewProperty({...newProperty, price: e.target.value})}
                        required
                        data-testid="property-price-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={newProperty.description}
                      onChange={(e) => setNewProperty({...newProperty, description: e.target.value})}
                      required
                      data-testid="property-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="address">Address *</Label>
                      <Input
                        id="address"
                        value={newProperty.address}
                        onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                        required
                        data-testid="property-address-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={newProperty.city}
                        onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                        required
                        data-testid="property-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Select value={newProperty.state} onValueChange={(value) => setNewProperty({...newProperty, state: value})}>
                        <SelectTrigger data-testid="property-state-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MS">Mississippi</SelectItem>
                          <SelectItem value="TN">Tennessee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="bedrooms">Bedrooms *</Label>
                      <Input
                        id="bedrooms"
                        type="number"
                        value={newProperty.bedrooms}
                        onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                        required
                        data-testid="property-bedrooms-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bathrooms">Bathrooms *</Label>
                      <Input
                        id="bathrooms"
                        type="number"
                        step="0.5"
                        value={newProperty.bathrooms}
                        onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                        required
                        data-testid="property-bathrooms-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="square_feet">Square Feet *</Label>
                      <Input
                        id="square_feet"
                        type="number"
                        value={newProperty.square_feet}
                        onChange={(e) => setNewProperty({...newProperty, square_feet: e.target.value})}
                        required
                        data-testid="property-sqft-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="create-property-btn">
                    Create Property
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogAutoDrafts posts={blogPosts} onEdit={setEditingBlog} onChanged={loadDashboardData} />

            {/* Formatting Guide */}
            <Card className="mb-6 bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">✍️ Quick Formatting Guide</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold mb-2">Text Formatting:</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• <code className="bg-white px-1">**bold text**</code> → <strong>bold text</strong></li>
                      <li>• <code className="bg-white px-1">*italic text*</code> → <em>italic text</em></li>
                      <li>• <code className="bg-white px-1"># Heading</code> → Large heading</li>
                      <li>• <code className="bg-white px-1">## Subheading</code> → Smaller heading</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Lists & Links:</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• <code className="bg-white px-1">- Item</code> → Bullet point</li>
                      <li>• <code className="bg-white px-1">1. Item</code> → Numbered list</li>
                      <li>• <code className="bg-white px-1">[Link text](url)</code> → Clickable link</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-white rounded border">
                  <p className="text-xs text-gray-600">
                    <strong>Pro Tip:</strong> Write naturally! Most formatting will be applied automatically. For images, upload via chat (paperclip icon 📎) and paste the URL.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Create Blog Post</CardTitle>
                <CardDescription>Add new content to the Bespoke blog</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateBlogPost} className="space-y-4">
                  <div>
                    <Label htmlFor="blog-title">Title *</Label>
                    <Input
                      id="blog-title"
                      value={newBlogPost.title}
                      onChange={(e) => setNewBlogPost({...newBlogPost, title: e.target.value})}
                      required
                      data-testid="blog-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt">Excerpt *</Label>
                    <Textarea
                      id="excerpt"
                      value={newBlogPost.excerpt}
                      onChange={(e) => setNewBlogPost({...newBlogPost, excerpt: e.target.value})}
                      rows={2}
                      required
                      data-testid="blog-excerpt-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      value={newBlogPost.content}
                      onChange={(e) => setNewBlogPost({...newBlogPost, content: e.target.value})}
                      rows={10}
                      required
                      data-testid="blog-content-input"
                      placeholder="Write your blog post content here. You can use simple formatting:

**Bold text** for bold
*Italic text* for italic
- Bullet points for lists
# Heading for titles

Write naturally and format as you go!"
                    />
                    <p className="text-xs text-gray-500 mt-1">Tip: Use **text** for bold, *text* for italic, and # for headings</p>
                  </div>

                  <div>
                    <Label htmlFor="image_url">Featured Image</Label>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          id="image_url"
                          type="url"
                          value={newBlogPost.image_url}
                          onChange={(e) => setNewBlogPost({...newBlogPost, image_url: e.target.value})}
                          placeholder="https://example.com/image.jpg or paste uploaded image URL"
                          data-testid="blog-image-input"
                        />
                        <label className="cursor-pointer">
                          <div className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm">Upload</span>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setUploadingImage(true);
                                toast.info('Image uploading... Please wait.');
                                // Simulate upload - in reality, you'd upload to a service
                                // For now, we'll guide users to upload via chat
                                setTimeout(() => {
                                  setUploadingImage(false);
                                  toast.info('Please upload images via the chat interface (paperclip icon) and paste the URL here.');
                                }, 1000);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-600">
                        📎 <strong>To upload images:</strong> Use the paperclip icon in the chat (bottom right) to upload, then paste the URL here.
                      </p>
                      {newBlogPost.image_url && (
                        <div className="mt-2">
                          <p className="text-sm font-semibold mb-2">Image Preview:</p>
                          <img 
                            src={newBlogPost.image_url} 
                            alt="Blog post preview" 
                            className="w-full max-w-md h-48 object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select value={newBlogPost.category} onValueChange={(value) => setNewBlogPost({...newBlogPost, category: value})}>
                        <SelectTrigger data-testid="blog-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="market_insights">Market Insights</SelectItem>
                          <SelectItem value="luxury_living">Luxury Living</SelectItem>
                          <SelectItem value="first_time_buyers">First-Time Buyers</SelectItem>
                          <SelectItem value="agent_education">Agent Education</SelectItem>
                          <SelectItem value="community">Community Highlights</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="author">Author *</Label>
                      <Input
                        id="author"
                        value={newBlogPost.author}
                        onChange={(e) => setNewBlogPost({...newBlogPost, author: e.target.value})}
                        required
                        data-testid="blog-author-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="published"
                      checked={newBlogPost.published}
                      onChange={(e) => setNewBlogPost({...newBlogPost, published: e.target.checked})}
                      className="h-4 w-4"
                      data-testid="blog-published-checkbox"
                    />
                    <Label htmlFor="published" className="font-normal">Publish immediately</Label>
                  </div>

                  <Button type="submit" className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="create-blog-btn">
                    Create Blog Post
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Existing Blog Posts */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Manage Blog Posts</CardTitle>
                <CardDescription>Edit or delete existing blog posts</CardDescription>
              </CardHeader>
              <CardContent>
                {blogPosts.length === 0 ? (
                  <p className="text-gray-500">No blog posts yet.</p>
                ) : (
                  <div className="space-y-4">
                    {blogPosts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold">{post.title}</h4>
                          <p className="text-sm text-gray-500">
                            {post.author} • {post.category} • {post.published ? '✅ Published' : '📝 Draft'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingBlog(post)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteBlogPost(post.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Blog Modal */}
            {editingBlog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <h3 className="text-xl font-bold mb-4">Edit Blog Post</h3>
                  <form onSubmit={handleUpdateBlogPost} className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={editingBlog.title}
                        data-testid="edit-blog-title-input"
                        onChange={(e) => setEditingBlog({...editingBlog, title: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Excerpt</Label>
                      <Textarea
                        value={editingBlog.excerpt || ''}
                        data-testid="edit-blog-excerpt-input"
                        onChange={(e) => setEditingBlog({...editingBlog, excerpt: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <Textarea
                        value={editingBlog.content}
                        data-testid="edit-blog-content-input"
                        onChange={(e) => setEditingBlog({...editingBlog, content: e.target.value})}
                        rows={10}
                        required
                      />
                    </div>
                    <div>
                      <Label>Image URL</Label>
                      <Input
                        value={editingBlog.image_url || ''}
                        data-testid="edit-blog-image-input"
                        onChange={(e) => setEditingBlog({...editingBlog, image_url: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Author</Label>
                      <Input
                        value={editingBlog.author || ''}
                        data-testid="edit-blog-author-input"
                        onChange={(e) => setEditingBlog({...editingBlog, author: e.target.value})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingBlog.published}
                        data-testid="edit-blog-published-checkbox"
                        onChange={(e) => setEditingBlog({...editingBlog, published: e.target.checked})}
                        className="h-4 w-4"
                      />
                      <Label className="font-normal">Published</Label>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setEditingBlog(null)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="edit-blog-save-btn">
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle>Upload Template</CardTitle>
                <CardDescription>Add marketing templates for agents to customize</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setUploadingTemplate(true);
                  try {
                    if (!newTemplate.file) {
                      toast.error('Please select a file');
                      return;
                    }
                    
                    const formData = new FormData();
                    formData.append('file', newTemplate.file);
                    formData.append('name', newTemplate.name);
                    formData.append('category', newTemplate.category);
                    if (newTemplate.canva_link) {
                      formData.append('canva_link', newTemplate.canva_link);
                    }
                    
                    await api.post('/templates/upload', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    
                    toast.success('Template uploaded successfully!');
                    setNewTemplate({ name: '', category: 'flyers', file: null });
                  } catch (error) {
                    console.error('Upload error:', error);
                    toast.error(error.response?.data?.detail || 'Failed to upload template');
                  } finally {
                    setUploadingTemplate(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      placeholder="e.g., Luxury Listing Flyer"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-category">Category *</Label>
                    <Select 
                      value={newTemplate.category} 
                      onValueChange={(value) => setNewTemplate({...newTemplate, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instagram_post">Instagram Post</SelectItem>
                        <SelectItem value="instagram_luxury_carousel">Instagram Luxury Carousel</SelectItem>
                        <SelectItem value="luxury_story">Luxury Story Posting</SelectItem>
                        <SelectItem value="general_post">General Post</SelectItem>
                        <SelectItem value="funny_post">Funny Post</SelectItem>
                        <SelectItem value="open_house">Open House</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="canva-link">Canva Template Link</Label>
                    <Input
                      id="canva-link"
                      placeholder="https://www.canva.com/design/XXXXX/template-link"
                      value={newTemplate.canva_link || ''}
                      onChange={(e) => setNewTemplate({...newTemplate, canva_link: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get the template link from Canva: Share → Template link → Copy
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="template-file">Template Preview Image *</Label>
                    <Input
                      id="template-file"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setNewTemplate({...newTemplate, file: e.target.files[0]})}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Upload PNG/JPG preview of the template
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="bg-[#BCA480] hover:bg-[#a89470]"
                    disabled={uploadingTemplate}
                  >
                    {uploadingTemplate ? 'Uploading...' : 'Upload Template'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Template Categories</CardTitle>
                <CardDescription>Current template library</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">Instagram Post</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">Instagram Luxury Carousel</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">Luxury Story Posting</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">General Post</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">Funny Post</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="font-semibold">Open House</p>
                    <p className="text-2xl text-[#BCA480]">0</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Upload Document</CardTitle>
                <CardDescription>Add documents for agent portal</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUploadDocument} className="space-y-4">
                  <div>
                    <Label htmlFor="doc-title">Document Title *</Label>
                    <Input
                      id="doc-title"
                      value={newDocument.title}
                      onChange={(e) => setNewDocument({...newDocument, title: e.target.value})}
                      required
                      data-testid="doc-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="doc-description">Description *</Label>
                    <Textarea
                      id="doc-description"
                      value={newDocument.description}
                      onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                      required
                      data-testid="doc-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="file-url">File URL *</Label>
                    <Input
                      id="file-url"
                      type="url"
                      value={newDocument.file_url}
                      onChange={(e) => setNewDocument({...newDocument, file_url: e.target.value})}
                      placeholder="https://..."
                      required
                      data-testid="doc-url-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload to Google Drive/Dropbox and paste the shareable link</p>
                  </div>

                  <div>
                    <Label htmlFor="doc-category">Category *</Label>
                    <Select value={newDocument.category} onValueChange={(value) => setNewDocument({...newDocument, category: value})}>
                      <SelectTrigger data-testid="doc-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="policy">Policy</SelectItem>
                        <SelectItem value="training">Training</SelectItem>
                        <SelectItem value="form">Form</SelectItem>
                        <SelectItem value="announcement">Announcement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="upload-doc-btn">
                    Upload Document
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle>Agent Applications</CardTitle>
                <CardDescription>Review and manage agent applications</CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-3">
                    {applications.map((app) => (
                      <div key={app.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold">{app.name}</h3>
                            <p className="text-sm text-gray-600">{app.email} | {app.phone}</p>
                            <p className="text-sm text-gray-600 mt-1">Experience: {app.years_experience} years</p>
                            <p className="text-sm text-gray-600">License: {app.license_number || 'Not provided'}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs rounded-full ${
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            app.status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {app.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-600 py-8">No applications yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent Websites Tab */}
          <TabsContent value="agent-sites">
            <div className="space-y-4">
              <AgentOfTheMonthAdmin />
              <AgentSitesAdmin />
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>Appointment Bookings</CardTitle>
                <CardDescription>Manage client appointment requests</CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length > 0 ? (
                  <div className="space-y-3">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold">{booking.name}</h3>
                            <p className="text-sm text-gray-600">{booking.email} | {booking.phone}</p>
                            <p className="text-sm text-gray-600 mt-1">Type: {booking.booking_type}</p>
                            <p className="text-sm text-gray-600">Date: {booking.date} at {booking.time}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs rounded-full ${
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-600 py-8">No bookings yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
